import React from "react";

import { Spinner } from "@amphore-dev/amphore-lib";
import { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";

import SSOService from "@/services/SSOService";
import UserService from "@/services/UserService";

import { useAuth } from "@/hooks/useAuth";

import { urlToFile } from "@/utils/UFiles";
import { takeReturnTo } from "@/utils/UReturnTo";

import { ssoKeys } from "@/constants/CQueryKeys";

/**
 * No answer from the API at all (offline, DNS, a request the browser
 * refused) is "unreachable"; the rest - an answer without a code, a bug -
 * "unexpected". Only axios errors say which: a TypeError could as well be
 * a bug as a failed fetch.
 */
export const ssoErrorCode = (error: unknown): "unreachable" | "unexpected" =>
	error instanceof AxiosError && !error.response
		? "unreachable"
		: "unexpected";

export const SSOCallback: React.FC = () => {
	const navigate = useNavigate();
	const { login } = useAuth();

	/**
	 * Every way out of the callback ends on a page the visitor can act
	 * on: the app, the register form, or the login page with a message
	 * it knows how to word (auth.errors.sso.*). An error the API answered
	 * carries its own code; anything else - the network, the browser
	 * refusing a request, a bug - is "unreachable" or "unexpected".
	 */
	const handleCallback = async () => {
		try {
			const data = await SSOService.handleCallback();

			if (data.status === "logged_in") {
				const user = await UserService.fetchMe(data.access_token);
				login(data, user);
				navigate(takeReturnTo());
				return user;
			}

			// A nicety, not a step: an avatar that cannot be downloaded
			// leaves the register form without one rather than stopping
			// the sign-up on "Failed to fetch".
			const avatar = await urlToFile(data.avatar).catch(() => null);

			navigate("/register", {
				state: {
					...data,
					fromSSO: true,
					avatar,
				},
			});
			return null;
		} catch (error) {
			console.error("SSO callback failed", error);

			const state =
				error instanceof AxiosError && error.response?.data?.error
					? error.response.data
					: { error: ssoErrorCode(error) };

			navigate("/login", { state: { ...state, fromSSO: true } });
			return null;
		}
	};

	useQuery({
		queryKey: ssoKeys.callback,
		queryFn: () => handleCallback(),
		retry: false,
	});

	// Every outcome navigates away: the spinner is all this page shows.
	return (
		<div className="flex w-full items-center justify-center">
			<Spinner className="mx-auto" size="lg" />
		</div>
	);
};
